import { useCallback, useEffect, useState } from 'react'

import { api } from '../api/client.js'

/**
 * A round the server has handed out.
 *
 * Always belongs to somebody: solo play is behind the login. Guests play in a
 * room, where the television owns the game and there is no round to hand out.
 */
export interface DealtRound<Config> {
  readonly roundId: string
  readonly seed: string
  readonly config: Config
}

export interface Round<Config> {
  /** Null while the server is being asked. Nothing should be dealt from nothing. */
  readonly round: DealtRound<Config> | null
  /** Asks for the next one. The old round is gone the moment this is called. */
  readonly again: () => void
  /** The server could not be reached. She should be told, not left waiting. */
  readonly failed: boolean
}

/**
 * Whether a reply is a round a game can actually use.
 *
 * Structure only — that there is an id, a seed and a config at all — and
 * deliberately nothing about what is *in* the config. The server decides what
 * a round means; a second opinion here would be a fifth place that knows the
 * shape of a maths round, and those drift.
 *
 * It exists because the reply used to be cast and never looked at, so the
 * first thing to read the round was the first thing to discover it was wrong:
 * `round.config.band` on a reply with no config threw, and a child got "Oops —
 * something went wobbly" for what is a bad answer from a server. The friendly
 * version already existed a few lines below — `failed` is rendered by every
 * game as "have another go in a moment" — and this is how a bad reply reaches
 * it instead.
 *
 * Checked here rather than in each of the six games, because a per-game guard
 * is six chances to forget the seventh.
 */
function isDealtRound<Config>(value: unknown): value is DealtRound<Config> {
  if (typeof value !== 'object' || value === null) return false

  const round = value as Record<string, unknown>

  return (
    typeof round.roundId === 'string' &&
    typeof round.seed === 'string' &&
    typeof round.config === 'object' &&
    round.config !== null
  )
}

/**
 * Asks the server for a round to play.
 *
 * The browser used to invent its own seed. It cannot any more: the server
 * replays a finished game to work out the score, and replaying only what a
 * client chooses to send is not the same as replaying what happened — the
 * mistakes could simply be left out, and the same winning round could be sent
 * twice. Issuing the seed here, once, is what closes both.
 *
 * `request` is serialised for the dependency rather than compared by identity,
 * so a caller passing an inline object cannot re-deal on every render.
 */
export function useRound<Config>(request: unknown): Round<Config> {
  const key = JSON.stringify(request)
  const [attempt, setAttempt] = useState(0)

  /** Which request the result in hand belongs to. */
  const asked = `${attempt}:${key}`

  const [result, setResult] = useState<Asked<Config>>({ asked, round: null, failed: false })

  // Derived rather than cleared in an effect. Clearing it there would render
  // once with the previous round still in hand — showing the last word under a
  // new heading — before the reset landed.
  const current = result.asked === asked ? result : { asked, round: null, failed: false }

  useEffect(() => {
    let live = true

    void api<unknown>('/api/rounds', { method: 'POST', body: key })
      .then((dealt) => {
        // The component may have moved on — a different board size chosen, or
        // unmounted. Dealing a round nobody asked for any more would swap the
        // game out from under whoever is looking at it.
        if (!live) return

        // A reply that is not a round is a failure to deal one, not a game.
        if (!isDealtRound<Config>(dealt)) {
          setResult({ asked, round: null, failed: true })
          return
        }

        setResult({ asked, round: dealt, failed: false })
      })
      .catch(() => {
        if (live) setResult({ asked, round: null, failed: true })
      })

    return () => {
      live = false
    }
  }, [asked, key])

  return {
    round: current.round,
    again: useCallback(() => setAttempt((each) => each + 1), []),
    failed: current.failed,
  }
}

interface Asked<Config> {
  readonly asked: string
  readonly round: DealtRound<Config> | null
  readonly failed: boolean
}
